import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "@material-ui/lab/Skeleton";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { ReactComponent as TrashIcon } from "../../../assets/svg/trash.svg";
import { ReactComponent as ScissorsIcon } from "../../../assets/svg/scissors.svg";
import { isRecordedVideoClip } from "../videoStudioTimeline.utils";

interface IThumbnailItem {
  effect: any;
  widthPercent: number;
  _handleShowVideoCutModal?: (effect: any) => void;
  _handleOnDeleteAddedFiles?: (effect: any) => void;
}

const ThumbnailItem = ({
  effect,
  widthPercent,
  _handleOnDeleteAddedFiles,
  _handleShowVideoCutModal,
}: IThumbnailItem) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const clipRef = useRef<HTMLDivElement>(null);

  const thumbUrls = useMemo(
    () =>
      (effect.thumbnails || []).map((thumb: { blob: Blob }) =>
        URL.createObjectURL(thumb.blob)
      ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effect.thumbnails, effect.uuid]
  );

  useEffect(
    () => () => {
      thumbUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    [thumbUrls]
  );

  useEffect(() => {
    if (!menuOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (clipRef.current && !clipRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [menuOpen]);

  const mirrorPreview = isRecordedVideoClip(effect);

  const stopSortDrag = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      ref={clipRef}
      className="timeline-clip-item"
      style={{
        width: `${widthPercent}%`,
        flexShrink: 0,
      }}
    >
      <div className="video-effect-wrap">
        {effect.thumbnails?.length ? (
          <>
            <div
              className={`timeline-clip-thumbs ${
                mirrorPreview ? "timeline-clip-thumbs--mirror" : ""
              }`}
            >
              {thumbUrls.map((url, index) => (
                <img
                  key={`${effect.uuid}-thumb-${index}`}
                  className="timeline-clip-thumb"
                  src={url}
                  alt=""
                  draggable={false}
                />
              ))}
            </div>

            <button
              type="button"
              className="timeline-clip-menu-btn"
              aria-label="Clip options"
              aria-expanded={menuOpen}
              onMouseDown={stopSortDrag}
              onClick={(event) => {
                stopSortDrag(event);
                setMenuOpen((open) => !open);
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </button>

            {menuOpen && (
              <ul
                className="thumbnail-menu-wrapper timeline-clip-dropdown"
                onMouseDown={stopSortDrag}
              >
                <li
                  onClick={() => {
                    setMenuOpen(false);
                    _handleShowVideoCutModal?.(effect);
                  }}
                >
                  <ScissorsIcon />
                  Trim
                </li>
                <li
                  onClick={() => {
                    setMenuOpen(false);
                    _handleOnDeleteAddedFiles?.(effect);
                  }}
                >
                  <TrashIcon />
                  Delete
                </li>
              </ul>
            )}
          </>
        ) : (
          <>
            <Skeleton
              variant="rect"
              animation="wave"
              className={`previews-img ${
                mirrorPreview ? "previews-img--mirror" : ""
              }`}
            />
            <button
              type="button"
              className="timeline-clip-menu-btn"
              aria-label="Clip options"
              aria-expanded={menuOpen}
              onMouseDown={stopSortDrag}
              onClick={(event) => {
                stopSortDrag(event);
                setMenuOpen((open) => !open);
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </button>

            {menuOpen && (
              <ul
                className="thumbnail-menu-wrapper timeline-clip-dropdown"
                onMouseDown={stopSortDrag}
              >
                <li
                  onClick={() => {
                    setMenuOpen(false);
                    _handleOnDeleteAddedFiles?.(effect);
                  }}
                >
                  <TrashIcon />
                  Delete
                </li>
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(ThumbnailItem);
